import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';
import { Scale } from 'lucide-react';
import { decimalToLocalizedString } from '../../../lib/Money.ts';
import { getCurrencySymbol } from '../../../lib/utils.ts';

type TotalsCheckProps = {
    grossDiffCents: number;
    vatDiffCents: number;
    isWithinTolerance: boolean;
    currency: string;
    onAdjustTotals: () => void;
};

const ROW_CLASS = 'flex min-w-0 items-center gap-1.5 text-[12px]';

function formatAmount(cents: number, currency: string): string {
    return `${getCurrencySymbol(currency)} ${decimalToLocalizedString(cents / 100)}`;
}

function formatLineDeviation(label: string, diffCents: number, currency: string): string {
    return `${label} ${formatAmount(Math.abs(diffCents), currency)} ${diffCents < 0 ? 'te hoog' : 'te laag'}`;
}

function formatDifference(grossDiffCents: number, vatDiffCents: number, currency: string): string {
    const grossDiff = formatAmount(Math.abs(grossDiffCents), currency);
    if (vatDiffCents === 0) {
        return grossDiff;
    }
    return `${grossDiff}, btw ${formatAmount(Math.abs(vatDiffCents), currency)}`;
}

export function TotalsCheck({
    grossDiffCents,
    vatDiffCents,
    isWithinTolerance,
    currency,
    onAdjustTotals,
}: TotalsCheckProps) {
    if (grossDiffCents === 0 && vatDiffCents === 0) {
        return (
            <span className={`${ROW_CLASS} font-medium text-green-700`} role="status">
                <CheckCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                Regels sluiten aan
            </span>
        );
    }

    if (grossDiffCents !== 0 && isWithinTolerance) {
        const diff = formatDifference(grossDiffCents, vatDiffCents, currency);
        return (
            <span className={`${ROW_CLASS} text-gray-600`} role="status">
                <Scale className="h-4 w-4 shrink-0 text-gray-600" aria-hidden="true" />
                <span>
                    Verschil {diff}{' '}
                    <span aria-hidden="true" className="text-gray-300">·</span>{' '}
                    <button
                        type="button"
                        onClick={onAdjustTotals}
                        aria-label={`Verschil ${diff} toevoegen aan laatste regel`}
                        className="rounded font-medium text-gray-600 underline underline-offset-2 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                        Toevoegen aan laatste regel
                    </button>
                </span>
            </span>
        );
    }

    const differences = [
        grossDiffCents !== 0 ? formatLineDeviation('totaal', grossDiffCents, currency) : null,
        vatDiffCents !== 0 ? formatLineDeviation('btw', vatDiffCents, currency) : null,
    ].filter((part): part is string => part !== null);

    return (
        <span className={`${ROW_CLASS} text-red-700`} role="status">
            <XCircleIcon className="h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
            <span>Regels: <span className="font-semibold tabular-nums">{differences.join(' · ')}</span></span>
        </span>
    );
}
